import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
  Autocomplete,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  golfTooltipHeaders,
  originsTooltip,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import { useSearchParams } from "react-router-dom";
import {
  getProductType,
  ProductEnum,
  SearchParams,
} from "../../../constants/login";
import { readLinkedValues } from "../../../excel/mapping/multiValueExport";

const formatOutletNamesForDisplay = (outlets: string[]): string =>
  outlets.map((name) => String(name).trim()).filter(Boolean).join(", ");

const QuickSaleCategories = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [searchParams] = useSearchParams();
  const isSpaLogin =
    getProductType(Number(searchParams.get(SearchParams.PRODUCT))) ===
    ProductEnum.SPA;
  const outletTooltip = isSpaLogin
    ? spaTooltipHeaders.quickSaleCategoryOutletName
    : golfTooltipHeaders.defaultOutlet;
  const [quickSaleCategory, setQuickSaleCategory] = useState<string | null>(
    null,
  );
  const [outletNames, setOutletNames] = useState<string[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [outletErr, setOutletErr] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [GolfQuickSaleCategories, setGolfQuickSaleCategories] = useState<any[]>(
    [],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outletList, setOutletList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    if (fieldValues) {
      const existingCategories =
        fieldValues[GolfPropertyEnum.quickSaleCategories] ?? [];
      setGolfQuickSaleCategories(
        existingCategories.map((item: any) => ({
          ...item,
          [DNDFieldsEnum.OUTLETNAME]: formatOutletNamesForDisplay(
            readLinkedValues(item?.[DNDFieldsEnum.OUTLETNAME]),
          ),
        })),
      );
      setOutletList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.outletSetup],
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(GolfQuickSaleCategories);
    editClicked &&
      setSelectedId(
        checkData(GolfQuickSaleCategories, selectedId) ? selectedId : null,
      );
  }, [GolfQuickSaleCategories]);

  const payload = {
    "Quick Sale Category": quickSaleCategory,
    "Outlet Name": formatOutletNamesForDisplay(outletNames),
    Active: active,
  };

  const validateFields = (): boolean => {
    setNameError(false);
    setOutletErr(false);
    let ok = true;

    if (!quickSaleCategory || quickSaleCategory.trim().length === 0) {
      setNameError(true);
      ok = false;
    } else if (
      !validateString(
        quickSaleCategory,
        validationLength.quickSaleCategory,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      ok = false;
    }

    if (!outletNames.length) {
      setOutletErr(true);
      ok = false;
    } else if (
      outletNames.some(
        (name) =>
          !validateString(
            String(name).trim(),
            validationLength.outletName,
            validationType.SPACE,
          ),
      )
    ) {
      setOutletErr(true);
      ok = false;
    }

    if (!ok) {
      setAlertMessage(
        !quickSaleCategory || quickSaleCategory.trim().length === 0
          ? "Quick sale category is mandatory"
          : !outletNames.length
            ? "At least one outlet is mandatory"
            : "Please correct the highlighted fields",
      );
      setAlertType("error");
      setOpenAlert(true);
    }

    return ok;
  };

  const updateGolfQuickSaleCategories = () => {
    if (!validateFields()) {
      return;
    }
    if (
      checkDuplicate(
        GolfQuickSaleCategories.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.QUICKSALECATEGORY,
        quickSaleCategory,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfQuickSaleCategories((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editGolfQuickSaleCategories(null);
    setAlertMessage(
      `${GolfPropertyEnum.quickSaleCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfQuickSaleCategories = () => {
    if (!validateFields()) {
      return;
    }
    if (
      checkDuplicate(
        GolfQuickSaleCategories,
        DNDFieldsEnum.QUICKSALECATEGORY,
        quickSaleCategory,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfQuickSaleCategories((prev: any[]) =>
      updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editGolfQuickSaleCategories(null);
  };

  const editGolfQuickSaleCategories = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setQuickSaleCategory(list?.[DNDFieldsEnum.QUICKSALECATEGORY] ?? null);
    setOutletNames(readLinkedValues(list?.[DNDFieldsEnum.OUTLETNAME]));
    setActive(list?.Active ?? true);
    setNameError(false);
    setOutletErr(false);
  };

  const deleteGolfQuickSaleCategories = (id: number) => {
    setGolfQuickSaleCategories(deleteItem(GolfQuickSaleCategories, id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Quick Sale Categories
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.quickSaleHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfQuickSaleCategories(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGolfQuickSaleCategories(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="20px">
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Quick Sale Category
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.quickSaleCategory)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="QuickSaleCategory"
                variant="outlined"
                placeholder="Quick Sale Category"
                value={quickSaleCategory ?? ""}
                helperText={
                  nameError ? validationMessages.quickSaleCategory : ""
                }
                error={nameError}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setQuickSaleCategory(e.target.value);
                  setNameError(false);
                }}
                sx={{ width: "100%" }}
              />
            </Stack>
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                multiple
                id="outletList"
                disablePortal
                options={removeSampleTestArray(outletList)}
                sx={{ width: "100%" }}
                value={outletNames}
                disabled={!outletList?.length}
                onChange={(_event, newValue: string[]) => {
                  setOutletNames(newValue);
                  setOutletErr(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Outlets *"
                    variant="outlined"
                    error={outletErr}
                    helperText={
                      outletErr ? validationMessages.fieldRequired : ""
                    }
                  />
                )}
              />
              <Tooltip title={tooltipComponent(outletTooltip)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId
                  ? updateGolfQuickSaleCategories()
                  : addGolfQuickSaleCategories()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Quick Sale Category
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">
              Quick Sale Categories Available
            </Typography>
            <Stack direction="column" gap="10px">
              {GolfQuickSaleCategories ? (
                <DraggableItemList
                  itemData={GolfQuickSaleCategories}
                  onOrderUpdate={(order) => setGolfQuickSaleCategories(order)}
                  onEdit={(info) => editGolfQuickSaleCategories(info)}
                  onDelete={(id) => deleteGolfQuickSaleCategories(id)}
                  code={DNDFieldsEnum.QUICKSALECATEGORY}
                  name={DNDFieldsEnum.OUTLETNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Quick Sale Categories Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default QuickSaleCategories;
